import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";
import { MonthDaysPanel, MonthStatusBadge } from "./MonthDaysPanel";
import { istToUtcIso, periodLabel } from "../../lib/regularisation";
import { ReopenDialog, ExtensionDialog } from "./MonthCloseDialogs";

function ResolveDialog({ target, employeeId, onClose, onDone }) {
    const [v, setV] = useState({ outcome: "Absent", ci: "", co: "", remarks: "" });
    const [busy, setBusy] = useState(false);
    if (!target) return null;
    const submit = async () => {
        if (v.remarks.trim().length < 10) { toast.error("Reason must be at least 10 characters."); return; }
        setBusy(true);
        try {
            await api.post("/regularization/resolve-date", {
                employee_id: employeeId, attendance_date: target.date, outcome: v.outcome, remarks: v.remarks,
                check_in_time: v.outcome === "times" ? istToUtcIso(v.ci, target.date) : null,
                check_out_time: v.outcome === "times" ? istToUtcIso(v.co, target.date) : null,
            });
            toast.success(`${formatDate(target.date)} resolved.`);
            onDone();
        } catch (err) {
            toast.error(formatApiError(err, "Could not resolve the date"));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="resolve-date-dialog">
                <DialogHeader>
                    <DialogTitle>Resolve {formatDate(target.date)}</DialogTitle>
                    <DialogDescription>Recorded: {target.day_status} — {target.reason}. The employee is notified with your reason.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Final attendance</Label>
                        <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={v.outcome} onChange={(e) => setV({ ...v, outcome: e.target.value })} data-testid="resolve-outcome">
                            <option value="Absent">Confirmed absence</option>
                            <option value="Present">Full day worked</option>
                            <option value="Half Day">Half day worked</option>
                            <option value="times">Enter check-in / check-out</option>
                        </select>
                    </div>
                    {v.outcome === "times" && (
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label className="text-xs">Check-in (IST)</Label><Input type="time" value={v.ci} onChange={(e) => setV({ ...v, ci: e.target.value })} data-testid="resolve-check-in" /></div>
                            <div><Label className="text-xs">Check-out (IST)</Label><Input type="time" value={v.co} onChange={(e) => setV({ ...v, co: e.target.value })} data-testid="resolve-check-out" /></div>
                        </div>
                    )}
                    <div><Label className="text-xs">Reason (required)</Label><Textarea rows={3} value={v.remarks} onChange={(e) => setV({ ...v, remarks: e.target.value })} data-testid="resolve-remarks" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} data-testid="resolve-confirm-btn">Resolve date</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function EmployeeMonthDrawer({ employeeId, period, onClose, onChanged, isAdmin = false }) {
    const navigate = useNavigate();
    const [m, setM] = useState(null);
    const [target, setTarget] = useState(null);
    const [reopen, setReopen] = useState(false);
    const [extend, setExtend] = useState(false);
    const load = useCallback(async () => {
        if (!employeeId) return;
        try {
            const { data } = await api.get(`/attendance/employee-month/${employeeId}`, { params: { period } });
            setM(data);
        } catch (err) {
            toast.error(formatApiError(err, "Could not load the month"));
        }
    }, [employeeId, period]);
    useEffect(() => { setM(null); load(); }, [load]);
    const locked = m?.month_status === "Finalised";

    return (
        <Sheet open={!!employeeId} onOpenChange={(o) => { if (!o) onClose(); }}>
            <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="employee-month-drawer">
                <SheetHeader>
                    <SheetTitle>{m?.employee?.full_name || "Employee"} · {periodLabel(period)}</SheetTitle>
                    <SheetDescription>{m ? <span className="flex items-center gap-2"><MonthStatusBadge status={m.month_status} testId="drawer-month-status" /> {m.employee.employee_code} · paid days {m.payroll.paid_days ?? "—"} ({m.payroll.basis})</span> : "Loading…"}</SheetDescription>
                </SheetHeader>
                {m && (
                    <div className="mt-4 space-y-5">
                        {isAdmin && (
                            <div className="flex flex-wrap gap-2" data-testid="drawer-admin-actions">
                                {locked && <Button size="sm" variant="outline" onClick={() => setReopen(true)} data-testid="drawer-reopen-btn">Reopen month</Button>}
                                {!locked && <Button size="sm" variant="outline" onClick={() => setExtend(true)} data-testid="drawer-extend-btn">Extend deadline for this employee</Button>}
                            </div>
                        )}
                        <MonthDaysPanel month={m} />
                        <div className="rounded-lg border border-slate-200">
                            <div className="px-3 py-2 text-sm font-semibold border-b border-slate-100">Pending dates ({m.pending_dates.length})</div>
                            {m.pending_dates.length === 0 && <div className="px-3 py-3 text-sm text-emerald-700" data-testid="drawer-no-pending">Nothing pending — ready to finalise once the month ends.</div>}
                            {m.pending_dates.map((p) => (
                                <div key={p.date} className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-slate-100 text-xs" data-testid={`drawer-pending-${p.date}`}>
                                    <span className="font-mono w-24">{formatDate(p.date)}</span>
                                    <span className="flex-1 text-slate-600">{p.day_status} — {p.reason}</span>
                                    {p.pending_with === "approver"
                                        ? <Button size="sm" variant="outline" onClick={() => navigate(`/regularization/requests?request_id=${p.request_id}`)} data-testid={`drawer-open-request-${p.date}`}>Open request</Button>
                                        : <><Badge className="bg-indigo-50 text-indigo-700 text-[10px]">With employee</Badge>
                                            {!locked && <Button size="sm" variant="outline" onClick={() => setTarget(p)} data-testid={`drawer-resolve-${p.date}`}>Resolve</Button>}</>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {m && <ReopenDialog open={reopen} period={period} employee={m.employee} onClose={() => setReopen(false)} onDone={() => { load(); onChanged?.(); }} />}
                {m && <ExtensionDialog open={extend} period={period} employee={m.employee} onClose={() => setExtend(false)} onSaved={() => { setExtend(false); load(); }} />}
                <ResolveDialog target={target} employeeId={employeeId} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); onChanged?.(); }} />
            </SheetContent>
        </Sheet>
    );
}

export default EmployeeMonthDrawer;
